<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  calculatePrice,
  calculatorPlans,
  config,
  decimal,
  integer,
  number,
} from '../config';
import SectionHeading from './SectionHeading.vue';

const emit = defineEmits(['select']);
const { t } = useI18n();
const selectedPlan = ref('growth');
const users = ref(config.plans.growth.users);
const accounts = ref(config.plans.growth.accounts);
const messages = ref(0);
const storage = ref(config.plans.growth.storage);
const plan = computed(() => config.plans[selectedPlan.value]);
const estimate = computed(() =>
  calculatePrice(
    selectedPlan.value,
    users.value,
    accounts.value,
    messages.value,
    storage.value
  )
);
const messageProgress = computed(() => `${(messages.value / 1000000) * 100}%`);

function setUsers(value) {
  users.value = Math.max(plan.value.users, integer(value));
}

function setAccounts(value) {
  accounts.value = Math.max(plan.value.accounts, integer(value));
}

function setMessages(value) {
  messages.value = integer(value);
}

function setStorage(value) {
  storage.value = Math.max(plan.value.storage, decimal(value));
}

function stepUsers(amount) {
  setUsers(users.value + amount);
}

function stepAccounts(amount) {
  setAccounts(accounts.value + amount);
}

function stepStorage(amount) {
  setStorage(storage.value + amount);
}

function changePlan(key) {
  selectedPlan.value = key;
  users.value = Math.max(users.value, config.plans[key].users);
  accounts.value = Math.max(accounts.value, config.plans[key].accounts);
  storage.value = Math.max(storage.value, config.plans[key].storage);
}

function selectPlan(key, capacity) {
  emit('select', key, capacity);
}

function requestCapacity() {
  selectPlan(selectedPlan.value, {
    users: users.value,
    accounts: accounts.value,
    messages: messages.value,
    storage: storage.value,
  });
}
</script>

<template>
  <section id="pricing" class="scroll-mt-24 py-20 md:py-28">
    <div class="mx-auto max-w-7xl px-5 md:px-8">
      <SectionHeading
        :eyebrow="t('pricing.eyebrow')"
        :title="t('pricing.title')"
        :description="t('pricing.description')"
      />

      <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <article
          v-for="(item, key) in config.plans"
          :key="key"
          class="relative flex flex-col rounded-2xl border p-6 transition-shadow hover:shadow-lg"
          :class="
            key === 'growth'
              ? 'border-woot-600 bg-woot-25 ring-1 ring-woot-600'
              : 'border-n-slate-4 bg-white'
          "
        >
          <span
            v-if="key === 'growth'"
            class="absolute -top-3 start-6 rounded-full bg-woot-600 px-4 py-1 text-xs font-bold text-white"
          >
            {{ t('pricing.recommended') }}
          </span>
          <h3 class="mb-5 text-xl font-bold">{{ t(`pricing.${key}`) }}</h3>
          <p class="text-3xl font-bold tracking-tight text-n-slate-12">
            {{ number(item.price) }}
          </p>
          <p class="mt-2 text-xs text-n-slate-8">{{ t('pricing.month') }}</p>
          <ul class="my-7 flex-1 space-y-4 text-sm text-n-slate-11">
            <li>{{ number(item.users) }} {{ t('pricing.users') }}</li>
            <li>{{ number(item.accounts) }} {{ t('pricing.accounts') }}</li>
            <li v-if="key === 'free'">
              {{ number(item.messages) }} {{ t('pricing.testMessages') }}
            </li>
            <li v-else>{{ t('pricing.sentMessageRate') }}</li>
            <li>
              {{
                key === 'free'
                  ? t('pricing.smallStorage')
                  : `${number(item.storage)} ${t('pricing.storage')}`
              }}
            </li>
            <li>{{ t('pricing.widget') }}</li>
            <li class="text-n-slate-8">{{ t('pricing.ai') }}</li>
          </ul>
          <button
            class="rounded-xl px-4 py-3 text-sm font-bold transition-colors focus-visible:ring-2 focus-visible:ring-woot-600 focus-visible:ring-offset-4"
            :class="
              key === 'growth'
                ? 'bg-woot-600 text-white hover:bg-woot-700'
                : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
            "
            @click="
              selectPlan(key, {
                users: item.users,
                accounts: item.accounts,
                messages: 0,
                storage: item.storage,
              })
            "
          >
            {{ key === 'free' ? t('freeStart') : t('activate') }}
          </button>
        </article>
      </div>

      <div
        class="mt-6 flex flex-col justify-between gap-6 rounded-2xl bg-n-slate-12 p-7 text-white md:flex-row md:items-center"
      >
        <div>
          <h3 class="text-xl font-bold">{{ t('pricing.enterpriseTitle') }}</h3>
          <p class="mt-3 text-sm leading-7 text-n-slate-5">
            {{ t('pricing.enterpriseDescription') }}
          </p>
        </div>
        <button
          class="shrink-0 rounded-xl bg-white px-6 py-3 text-sm font-bold text-n-slate-12 hover:bg-n-slate-3"
          @click="
            selectPlan('enterprise', {
              users: 1,
              accounts: 1,
              messages: 0,
              storage: 0,
            })
          "
        >
          {{ t('consult') }}
        </button>
      </div>

      <div
        class="mt-10 overflow-hidden rounded-3xl border border-n-slate-4 bg-white shadow-sm"
      >
        <div class="border-b border-n-slate-4 bg-n-slate-2 px-6 py-7 md:px-9">
          <h3 class="text-2xl font-bold text-n-slate-12">
            {{ t('pricing.calculator') }}
          </h3>
          <p class="mt-3 text-sm leading-7 text-n-slate-8">
            {{ t('pricing.calculatorDescription') }}
          </p>
        </div>

        <div class="grid gap-8 p-6 md:grid-cols-[1.1fr_0.9fr] md:p-9">
          <div class="space-y-6">
            <label class="block text-sm font-medium text-n-slate-12">
              {{ t('pricing.planLabel') }}
              <select
                :value="selectedPlan"
                class="mt-2 w-full rounded-xl border border-n-slate-4 bg-white p-3 outline-none transition focus:border-woot-600 focus:ring-2 focus:ring-woot-600/20"
                @change="changePlan($event.target.value)"
              >
                <option v-for="key in calculatorPlans" :key="key" :value="key">
                  {{ t(`pricing.${key}`) }}
                </option>
              </select>
            </label>

            <div class="grid gap-5 sm:grid-cols-2">
              <label class="block text-sm font-medium text-n-slate-12">
                {{ t('pricing.usersLabel') }}
                <div class="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    class="stepper-button"
                    @click="stepUsers(-1)"
                  >
                    −
                  </button>
                  <input
                    :value="users"
                    inputmode="numeric"
                    class="capacity-input"
                    @change="setUsers($event.target.value)"
                  />
                  <button
                    type="button"
                    class="stepper-button"
                    @click="stepUsers(1)"
                  >
                    +
                  </button>
                </div>
                <span class="field-hint">{{ t('pricing.usersHint') }}</span>
              </label>

              <label class="block text-sm font-medium text-n-slate-12">
                {{ t('pricing.accountsLabel') }}
                <div class="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    class="stepper-button"
                    @click="stepAccounts(-1)"
                  >
                    −
                  </button>
                  <input
                    :value="accounts"
                    inputmode="numeric"
                    class="capacity-input"
                    @change="setAccounts($event.target.value)"
                  />
                  <button
                    type="button"
                    class="stepper-button"
                    @click="stepAccounts(1)"
                  >
                    +
                  </button>
                </div>
                <span class="field-hint">{{ t('pricing.accountsHint') }}</span>
              </label>
            </div>

            <div class="rounded-2xl border border-n-slate-4 bg-n-slate-2 p-4">
              <div class="flex items-center justify-between gap-3">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('pricing.messagesLabel') }}
                </label>
                <span
                  class="rounded-lg bg-white px-3 py-1.5 text-sm font-bold text-woot-600 shadow-sm"
                >
                  {{ number(messages) }}
                </span>
              </div>
              <input
                :value="messages"
                type="range"
                min="0"
                max="1000000"
                step="100"
                class="message-range mt-5 w-full"
                :style="{ '--message-progress': messageProgress }"
                @input="setMessages($event.target.value)"
              />
              <div class="mt-2 flex justify-between text-[11px] text-n-slate-8">
                <span>{{ number(0) }}</span>
                <span>{{ number(1000000) }}</span>
              </div>
              <div class="mt-4 flex items-center gap-3">
                <input
                  :value="messages"
                  inputmode="numeric"
                  class="capacity-input text-start"
                  @change="setMessages($event.target.value)"
                />
                <p class="text-xs leading-6 text-n-slate-8">
                  {{ t('pricing.messagesHint') }}
                </p>
              </div>
            </div>

            <label class="block text-sm font-medium text-n-slate-12">
              {{ t('pricing.storageLabel') }}
              <div class="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  class="stepper-button"
                  @click="stepStorage(-1)"
                >
                  −
                </button>
                <input
                  :value="storage"
                  inputmode="decimal"
                  class="capacity-input"
                  @change="setStorage($event.target.value)"
                />
                <button
                  type="button"
                  class="stepper-button"
                  @click="stepStorage(1)"
                >
                  +
                </button>
              </div>
              <span class="field-hint">{{ t('pricing.storageHint') }}</span>
            </label>
          </div>

          <aside
            class="self-start rounded-2xl bg-n-slate-12 p-5 text-white shadow-xl shadow-n-slate-12/15 md:p-6"
          >
            <h4 class="mb-4 text-lg font-bold text-white">
              {{ t('pricing.total') }}
            </h4>
            <div class="space-y-3 text-sm text-n-slate-5">
              <div class="result-row">
                <span>{{ t('pricing.basePrice') }}</span
                ><strong class="text-white">{{
                  number(estimate.basePrice)
                }}</strong>
              </div>
              <div class="result-row">
                <span
                  >{{ number(estimate.extraUsers) }}
                  {{ t('pricing.extraUsers') }}</span
                ><strong class="text-white">{{
                  number(estimate.extraUsersCost)
                }}</strong>
              </div>
              <div class="result-row">
                <span
                  >{{ number(estimate.extraAccounts) }}
                  {{ t('pricing.extraAccounts') }}</span
                ><strong class="text-white">{{
                  number(estimate.extraAccountsCost)
                }}</strong>
              </div>
              <div class="result-row">
                <span
                  >{{ number(messages) }} {{ t('pricing.sentMessages') }}</span
                ><strong class="text-white">{{
                  number(estimate.messagesCost)
                }}</strong>
              </div>
              <div class="result-row">
                <span
                  >{{ number(estimate.extraStorage) }}
                  {{ t('pricing.extraStorage') }}</span
                ><strong class="text-white">{{
                  estimate.storageInquiry
                    ? t('pricing.storageInquiry')
                    : number(estimate.extraStorageCost)
                }}</strong>
              </div>
            </div>
            <div class="mt-5 border-t border-n-slate-10 pt-5">
              <p class="text-xs leading-6 text-n-slate-5">
                {{
                  estimate.storageInquiry
                    ? t('pricing.totalWithoutStorage')
                    : t('pricing.total')
                }}
              </p>
              <p class="mt-1 text-2xl font-bold text-woot-600">
                {{
                  number(
                    estimate.storageInquiry
                      ? estimate.totalWithoutStorage
                      : estimate.total
                  )
                }}
                <span class="text-xs font-normal text-n-slate-5">{{
                  t('pricing.month')
                }}</span>
              </p>
            </div>
            <p class="mt-4 text-xs leading-6 text-n-slate-5">
              {{ t('pricing.estimateNote') }}
            </p>
            <button
              class="mt-5 w-full rounded-xl bg-white px-4 py-3 text-sm font-bold text-woot-600 transition hover:bg-n-slate-3 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-n-slate-12"
              @click="requestCapacity"
            >
              {{ t('pricing.select') }}
            </button>
          </aside>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.stepper-button {
  @apply flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-n-slate-4 bg-white text-lg text-n-slate-11 transition hover:border-woot-600 hover:text-woot-600;
}

.capacity-input {
  @apply h-11 min-w-0 w-full rounded-xl border border-n-slate-4 bg-white px-3 text-center text-sm text-n-slate-12 outline-none transition focus:border-woot-600 focus:ring-2 focus:ring-woot-600/20;
}

.field-hint {
  @apply mt-2 block text-xs font-normal leading-6 text-n-slate-8;
}

.result-row {
  @apply flex items-center justify-between gap-3;
}

.message-range {
  appearance: none;
  direction: rtl;
  height: 0.35rem;
  border-radius: 9999px;
  background: linear-gradient(
    to left,
    #2563eb 0%,
    #2563eb var(--message-progress),
    #dbeafe var(--message-progress),
    #dbeafe 100%
  );
  outline: none;
}

.message-range::-webkit-slider-thumb {
  appearance: none;
  width: 1.15rem;
  height: 1.15rem;
  border: 3px solid white;
  border-radius: 9999px;
  background: #2563eb;
  box-shadow: 0 1px 5px rgb(15 23 42 / 25%);
  cursor: pointer;
}

.message-range::-moz-range-thumb {
  width: 1.15rem;
  height: 1.15rem;
  border: 3px solid white;
  border-radius: 9999px;
  background: #2563eb;
  box-shadow: 0 1px 5px rgb(15 23 42 / 25%);
  cursor: pointer;
}
</style>
